import { action, computed, observable } from 'mobx';
import {
    IProductComparisonItem,
    IProductComparisonModule
} from '../../exported/product-comparison-module/ProductComparisonModule';
import { ProductRepository } from '../repositories/ProductRepository';
import { Product } from '../domain/entities/Product';
import { QueryFragments } from '~api/QueryFragments';
import { IFeatures } from '~constants/Features';
import { events, on } from '@estee/elc-service-bus';
import { ProductGridService } from '~domain/services/ProductGridService';
import { IItem } from '~api/interfaces/IItem';
import { IParamsById } from '~api/interfaces/IParamsById';
import { getItemsFromParamsById } from '~api/utils/Filters';
import { ViewController } from '@estee/elc-service';

interface IFavorites {
    skuBaseIds: number[];
}

interface IFavoriteUpdatedEventData {
    skuBaseId: number;
    isFavorite: boolean;
    isAlreadyAdded?: boolean;
}

export class ProductComparisonModuleViewController extends ViewController {
    @observable public productRepository: ProductRepository;
    @observable public isLoaded: boolean = false;
    @observable public products: Product[] = [];
    @observable private queryFragments: QueryFragments;
    @observable public productGridService: ProductGridService;

    constructor(
        productRepository: ProductRepository,
        queryFragments: QueryFragments,
        productGridService: ProductGridService
    ) {
        super();
        this.productRepository = productRepository;
        this.queryFragments = queryFragments;
        this.productGridService = productGridService;
    }

    @action
    public loaded = async (props: IProductComparisonModule) => {
        const { items, collectionItems, features } = props.config;
        let itemsParams: IItem[] = [];
        const paramsById: IParamsById = {
            productIds: [],
            skuIds: []
        };
        this.queryFragments.setFeatures(<IFeatures>features);
        const queryFragments = this.queryFragments.queryFragments;

        if (items && items.length) {
            itemsParams = items;
        } else if (collectionItems && collectionItems.length > 0) {
            collectionItems.forEach((collectionItem) => {
                if (collectionItem.productId) {
                    paramsById.productIds?.push(collectionItem.productId);
                } else if (collectionItem.skuId) {
                    paramsById.skuIds?.push(collectionItem.skuId);
                }
            });
            itemsParams = getItemsFromParamsById(paramsById);
        }

        this.products = await this.productRepository.getProductsByItems(itemsParams, {
            queryFragments
        });
        this.products = this.productGridService.filterSkus(this.products, itemsParams);

        this.isLoaded = true;
    };

    @computed
    public get data() {
        return {
            products: this.products
        };
    }

    public getProductById(param: IProductComparisonItem) {
        if (param.productId) {
            return this.products.find((prod: Product) => prod.productId === param.productId);
        } else if (param.skuId) {
            return this.products.find((prod: Product) =>
                prod.skus.find((sku) => sku.skuId === param.skuId)
            );
        }
    }

    @action
    private updateFavorites = (favoriteSkuBaseIds: number[], isFavorite: boolean = true) => {
        this.products = this.products.map((product) => {
            product.initialSkus = product.skus.map((sku) => {
                const {
                    perlgem: { SKU_BASE_ID: skuBaseId }
                } = sku;
                if (favoriteSkuBaseIds.includes(skuBaseId)) {
                    sku.isFavorite = isFavorite;
                }

                return sku;
            });

            return product;
        });
    };

    @on(events.FAVORITES_LOADED)
    public initFavorites = ({ skuBaseIds }: IFavorites) => {
        this.updateFavorites(skuBaseIds);
    };

    @on(events.FAVORITE_STATUS_UPDATED)
    public updateFavorite = ({
        skuBaseId,
        isFavorite,
        isAlreadyAdded
    }: IFavoriteUpdatedEventData) => {
        if (!isAlreadyAdded) {
            this.updateFavorites([skuBaseId], isFavorite);
        }
    };
}
